import React, {
    useState, useContext, useCallback, useEffect, useMemo
} from 'react';
import _ from 'lodash';
import classNames from 'classnames';
import { withRouter } from 'react-router-dom';
import { PhoneNumber } from 'gw-components-platform-react';
import {
    Button,
    InputField,
    Icon,
    DropdownSelectField,
    Link,
    ModalNextProvider
} from '@jutro/components';
import { TranslatorContext } from '@jutro/locale';
import { WizardPage, wizardProps, WizardPageTemplate } from 'gw-portals-wizard-react';
import { useDependencies } from 'gw-portals-dependency-react';
import { withAuthenticationContext } from 'gw-digital-auth-react';
import { claimsMessages } from 'gw-capability-claim-react';
import { messages as commonMessages } from 'gw-platform-translations';
import { useValidation } from 'gw-portals-validation-react';
import { ViewModelServiceContext, ViewModelForm } from 'gw-portals-viewmodel-react';
import metadata from './AdditionalInformationPage.metadata.json5';
import styles from './AdditionalInformationPage.module.scss';
import messages from '../../FNOL.messages';
import Claim from '../../models/Claim';

const ROLES_TO_FILTER = ['witness', 'other'];

const getPhoneFieldBasedOnPrimaryPhoneType = (relatedContact) => {
    let primaryPhoneType;
    switch (relatedContact.contact.primaryPhoneType) {
        case 'home':
            primaryPhoneType = 'homeNumber';
            break;
        case 'work':
            primaryPhoneType = 'workNumber';
            break;
        case 'mobile':
            primaryPhoneType = 'cellNumber';
            break;
        default:
            primaryPhoneType = 'homeNumber';
    }
    return primaryPhoneType;
};

function FNOLAdditionalInformationPage(props) {
    const {
        wizardData: claimVM, updateWizardData, authHeader
    } = props;
    const { ClaimDownloadService } = useDependencies('ClaimDownloadService');
    const { ClaimService } = useDependencies('ClaimService');
    const translator = useContext(TranslatorContext);
    const viewModelService = useContext(ViewModelServiceContext);
    const [claimObj, updateClaimObj] = useState({});
    const [showLoader, setShowLoader] = useState(false);
    const { onValidate, isComponentValid, registerComponentValidation } = useValidation(
        'FNOLAdditionalInformationPage'
    );

    const showInjuredColumn = useMemo(
        () => !['InlandMarine', 'GeneralLiability', 'BusinessOwners'].includes(_.get(claimVM, 'policy.policyType.value')),
        // Policy type won't change on re-renders
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const writeValue = useCallback(
        (value, path) => {
            _.set(claimVM, path, value);
            updateWizardData(claimVM);
            _.set(claimObj, 'relatedContacts', claimVM.relatedContacts.value);
        },
        [claimObj, claimVM, updateWizardData]
    );

    const handleError = useCallback(
        (title, message) => {
            return ModalNextProvider.showAlert({
                title: title,
                message: message,
                status: 'error',
                icon: 'mi-error-outline',
                confirmButtonText: commonMessages.ok
            }).catch(_.noop);
        }, []
    );

    useEffect(() => {
        updateClaimObj(new Claim(claimVM.value));
        // execute once
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getFirstLastNameData = useCallback(
        (item, rowId, property) => {
            const { id, path } = property;
            const claimVMPath = `relatedContacts.value[${rowId}].${path}`;
            let setDisable = item.contact.publicID
                ? 'disabled'
                : '';
            if(path === 'perspective') {
                setDisable = '';
            }
            return (
                <InputField
                    id={`${id}_${rowId}`}
                    className={styles.tableColumn}
                    path={claimVMPath}
                    onValueChange={writeValue}
                    value={_.get(claimVM, claimVMPath)}
                    disabled={setDisable}
                />
            );
        },
        [claimVM, writeValue]
    );

    const getOfficialNameData = useCallback(
        (item, rowId, property) => {
            const { id, path } = property;
            const claimVMPath = `officials.value[${rowId}].${path}`;
            return (
                <InputField
                    id={`${id}_${rowId}`}
                    className={styles.tableColumn}
                    path={claimVMPath}
                    onValueChange={writeValue}
                    value={_.get(claimVM, claimVMPath)}
                />
            );
        },
        [claimVM, writeValue]
    );

    const filteredRoleTypeList = useMemo(() => {
        const typeList = viewModelService.create(
            {},
            'cc',
            'edge.capabilities.claim.fnol.dto.FnolRelatedContactDTO'
        );
        const filteredList = typeList.role.aspects.availableValues
            .filter((typeCode) => {
                return ROLES_TO_FILTER.includes(typeCode.code);
            })
            .map((typeCode) => {
                return {
                    code: typeCode.code,
                    name: translator({
                        id: typeCode.name,
                        defaultMessage: typeCode.name
                    })
                };
            });
        return filteredList;
    }, [translator, viewModelService]);

    const filteredWitnessPositionTypeList = useMemo(() => {
        const typeList = viewModelService.create(
            {},
            'cc',
            'edge.capabilities.claim.fnol.dto.FnolRelatedContactDTO'
        );
        const filteredList = typeList.witnessPosition.aspects.availableValues
            .map((typeCode) => {
                return {
                    code: typeCode.code,
                    name: translator({
                        id: typeCode.name,
                        defaultMessage: typeCode.name
                    })
                };
            });
        return filteredList;
    }, [translator, viewModelService]);

    const filteredWitnessStatementTypeList = useMemo(() => {
        const typeList = viewModelService.create(
            {},
            'cc',
            'edge.capabilities.claim.fnol.dto.FnolRelatedContactDTO'
        );
        const filteredList = typeList.witnessStatementInd.aspects.availableValues
            .map((typeCode) => {
                return {
                    code: typeCode.code,
                    name: translator({
                        id: typeCode.name,
                        defaultMessage: typeCode.name
                    })
                };
            });
        return filteredList;
    }, [translator, viewModelService]);

    const filteredOfficialTypeTypeList = useMemo(() => {
        const typeList = viewModelService.create(
            {},
            'cc',
            'edge.capabilities.claim.fnol.dto.OfficialDTO'
        );
        const filteredList = typeList.officialType.aspects.availableValues
            .map((typeCode) => {
                return {
                    code: typeCode.code,
                    name: translator({
                        id: typeCode.name,
                        defaultMessage: typeCode.name
                    })
                };
            });
        return filteredList;
    }, [translator, viewModelService]);

    const getInvolvementInjuredData = useCallback(
        (item, rowId, property) => {
            const { id, path } = property;
            let availableValues;
            let claimVMPath = `relatedContacts.value[${rowId}].${path}`;
            if (id === 'role') {
                availableValues = filteredRoleTypeList;
            } else if (id === 'witnessPosition') {
                availableValues = filteredWitnessPositionTypeList;
            } else if (id === 'witnessStatement') {
                availableValues = filteredWitnessStatementTypeList;
            } else if (id === 'officialTypeVal') {
                availableValues = filteredOfficialTypeTypeList;
                claimVMPath = `officials.value[${rowId}].${path}`;
            } else {
                availableValues = [
                    {
                        code: 'true',
                        name: translator(messages.fnolYes)
                    },
                    {
                        code: 'false',
                        name: translator(messages.fnolNo)
                    }
                ];
            }
            return (
                <DropdownSelectField
                    id={`${id}_${rowId}`}
                    alwaysShowPlaceholder={false}
                    availableValues={availableValues}
                    path={claimVMPath}
                    onValueChange={writeValue}
                    value={_.get(claimVM, claimVMPath)}
                    className={styles.involvmentInjuredDropDown}
                />
            );
        },
        [claimVM, filteredRoleTypeList, translator, writeValue]
    );

    const getPhoneNumberData = useCallback(
        (item, rowId) => {
            const phoneField = getPhoneFieldBasedOnPrimaryPhoneType(item);
            const claimVMPath = `relatedContacts.value[${rowId}].contact.${phoneField}`;
            const setDisable = item.contact.publicID
                ? 'disabled'
                : '';
            return (
                <PhoneNumber
                    id={`${item.primaryPhoneID}_${rowId}`}
                    alwaysShowMask
                    onValueChange={writeValue}
                    path={claimVMPath}
                    disabled={setDisable}
                    value={_.get(claimVM, claimVMPath)}
                />
            );
        },
        [claimVM, writeValue]
    );

    const getFileExtension = useCallback((name) => {
        const extension = name.split('.').pop();
        return extension || '';
    }, []);

    const getFileIcon = useCallback((extension) => {
        switch (extension) {
            case 'doc':
            case 'docx':
            case 'txt':
            case 'docm':
                return 'file-word';
            case 'xlt':
            case 'xlsx':
            case 'xlsm':
            case 'xls':
                return 'file-excel';
            case 'gif':
            case 'png':
            case 'jpeg':
                return 'file-image';
            case 'pdf':
                return 'file-pdf';
            default:
                return 'file';
        }
    }, []);

    const changeUploadDocmentImageType = useCallback(
        (documentUploadItem) => {
            const extension = getFileExtension(documentUploadItem.docUID).toLowerCase() || '';
            return `${getFileIcon(extension)}`;
        },
        [getFileExtension, getFileIcon]
    );

    const showDocumentDeleteIcon = useCallback((documentUploadItem) => {
        if (documentUploadItem.canDelete) {
            return true;
        }
        return false;
    }, []);

    const onDeleteDocumentIcon = useCallback(
        (e, item) => {
            e.preventDefault();
            const { claimNumber, publicID, name } = item;
            ModalNextProvider.showConfirm({
                title: claimsMessages.confirmationToRemoveTitle,
                message: translator(messages.fnolRemoveUploadedDocument, { documentName: name }),
                status: 'warning',
                icon: 'mi-error-outline',
                confirmButtonText: claimsMessages.removeConfirmationYes,
                cancelButtonText: claimsMessages.removeConfirmationNo
            }).then(async (results) => {
                if (results === 'cancel') {
                    return _.noop();
                }
                setShowLoader(true);
                try {
                    const isDeleteItem = await ClaimService.claimsRemoveDocument(
                        [claimNumber, publicID],
                        authHeader
                    );
                    if (isDeleteItem === false) {
                        setShowLoader(false);
                        return handleError(
                            claimsMessages.deletionFailedTitle,
                            claimsMessages.deletionFailedMessage
                        );
                    }
                    const documentsArray = _.get(claimVM, 'documents.value');
                    const updatedDocArray = documentsArray.filter((documentObj) => {
                        return documentObj.publicID !== publicID;
                    });
                    const newClaimVM = _.clone(claimVM);
                    _.set(newClaimVM, 'documents.value', updatedDocArray);
                    updateWizardData(newClaimVM);
                    setShowLoader(false);
                    return true;
                } catch (documentDeletionError) {
                    setShowLoader(false);
                    return handleError(
                        claimsMessages.removeServiceFailedTitle,
                        claimsMessages.removeServiceFailedMessage
                    );
                }
            }, _.noop);
        },
        [translator, ClaimService, authHeader, claimVM, updateWizardData, handleError]
    );

    const handleDocumentDownload = useCallback(
        (item) => {
            if (_.isNil(item)) {
                return null;
            }
            const { workingPublicID, sessionID } = item;
            return ClaimDownloadService
                .getClaimDocument(workingPublicID, sessionID);
        },
        [ClaimDownloadService]
    );

    const generateMoreInfoDocumentsOverrides = useCallback(() => {
        const documentUploadPath = 'value.documents';
        const documentUploadItems = _.get(claimVM, documentUploadPath, []);
        const overrides = documentUploadItems.map((documentUploadItem, index) => {
            return {
                [`documentLeftSection${index}`]: {
                    icon: changeUploadDocmentImageType(documentUploadItem)
                },
                [`documentNameLink${index}`]: {
                    href: handleDocumentDownload(documentUploadItem)
                },
                [`documentDeleteSection${index}`]: {
                    onClick: (e) => {
                        onDeleteDocumentIcon(e, documentUploadItem);
                    }
                },
                [`documentDeleteIcon${index}`]: {
                    visible: showDocumentDeleteIcon(documentUploadItem)
                }
            };
        });
        return Object.assign({}, ...overrides);
    }, [
        changeUploadDocmentImageType,
        claimVM,
        handleDocumentDownload,
        onDeleteDocumentIcon,
        showDocumentDeleteIcon
    ]);

    const getContactsRelatedPersons = useCallback((contactsItem) => {
        if (contactsItem.subtype === 'Person' && contactsItem.lastName) {
            return `${contactsItem.firstName} ${contactsItem.lastName}`;
        }
        return '';
    }, []);

    const generateMoreInfoAddPersonOverrides = useCallback(() => {
        const contactsPath = 'value.contacts';
        const contactsItems = _.get(claimVM, contactsPath, []);
        let availableRelatedContacts = [];
        if (!_.isEmpty(claimObj)) {
            availableRelatedContacts = claimObj.availableRelatedContacts().filter((contact) => {
                return contact.subtype === 'Person' && contact.firstName;
            });
        }

        if (!contactsItems.length) {
            return {};
        }
        const overrides = contactsItems.map((contactItem, index) => {
            return {
                [`contactItems${index}`]: {
                    message: getContactsRelatedPersons(contactItem)
                },
                [`contactItemsDropDown${index}`]: {
                    visible: _.includes(availableRelatedContacts, contactItem)
                }
            };
        });
        return Object.assign({}, ...overrides);
    }, [claimVM, claimObj, getContactsRelatedPersons]);

    const removeContactItem = useCallback(
        (e, rowId) => {
            e.preventDefault();
            const { relatedContacts } = claimVM;
            const relatedContactToDelete = relatedContacts.value.filter((relatedContact, index) => {
                return index === rowId;
            });
            claimObj.removeRelatedContact(relatedContactToDelete[0].contact);
            _.set(claimVM.value, 'relatedContacts', claimObj.relatedContacts);
            updateWizardData(claimVM);
        },
        [claimVM, claimObj, updateWizardData]
    );

    const removeContactRow = useCallback(
        (item, index) => {
            return (
                <Link
                    to="/"
                    onClick={(e) => removeContactItem(e, index, item)}
                    className={classNames(styles.deleteSection, styles.deleteRow)}
                >
                    <Icon icon="mi-delete" className={styles.deleteIcon} />
                </Link>
            );
        },
        [removeContactItem]
    );

    const removeOfficialItem = useCallback(
        (e, rowId) => {
            e.preventDefault();
            const { officials } = claimVM;
            claimObj.removeOfficial(rowId);
            _.set(claimVM.value, 'officials', claimObj.officials);
            updateWizardData(claimVM);
        },
        [claimVM, claimObj, updateWizardData]
    );

    const removeOfficialRow = useCallback(
        (item, index) => {
            return (
                <Link
                    to="/"
                    onClick={(e) => removeOfficialItem(e, index, item)}
                    className={classNames(styles.deleteSection, styles.deleteRow)}
                >
                    <Icon icon="mi-delete" className={styles.deleteIcon} />
                </Link>
            );
        },
        [removeOfficialItem]
    );

    const generateNewWitnessRow = useCallback(
        (event) => {
            event.preventDefault();
            const { contacts } = claimVM;
            const getCurrentMenuItemID = event.currentTarget.id;
            if (getCurrentMenuItemID.indexOf('anotherPersonLink') === -1) {
                const getIndex = parseInt(getCurrentMenuItemID.match(/[0-9]/gi), 10);
                const selectedContact = contacts.value[getIndex];
                claimObj.addRelatedContact(selectedContact);
                _.set(claimVM.value, 'relatedContacts', claimObj.relatedContacts);
            } else {
                claimObj.addRelatedContact(null);
                _.set(claimVM.value, 'relatedContacts', claimObj.relatedContacts);
            }
            updateWizardData(claimVM);
        },
        [claimVM, updateWizardData, claimObj]
    );

    const generateNewOfficialRow = useCallback(
        (event) => {
            event.preventDefault();
            claimObj.addOfficialContact(null);
            _.set(claimVM.value, 'officials', claimObj.officials);
            updateWizardData(claimVM);
        },
        [claimVM, updateWizardData, claimObj]
    );

    const generateAddPersonMenuDropDown = useCallback(
        (dropDownProps, toggleMenu) => {
            const { isOpen } = dropDownProps;
            const onAddPersonClick = () => {
                toggleMenu(!isOpen);
            };
            return (
                <Button onClick={onAddPersonClick} icon="mi-expand_more" iconPosition="right">
                    {translator(messages.fnolAddPerson)}
                </Button>
            );
        },
        [translator]
    );

    const claimsDocUploadToken = useCallback(async () => {
        try {
            const uploadTokenID = await ClaimService.claimsDocUploadToken([], authHeader);
            return uploadTokenID;
        } catch (e) {
            return handleError(
                commonMessages.errorUploadTitle,
                commonMessages.errorGenerateUploadToken
            );
        }
    }, [ClaimService, authHeader, handleError]);

    const onUploadDocument = useCallback(
        async (file) => {
            setShowLoader(true);
            const documentMetaDataTemplate = {
                DocUID: '001',
                DocumentType: 'fnol',
                SecurityType: 'unrestricted',
                Status: 'approved',
                Author: 'Policy Holder',
                claimNumber: claimVM.claimNumber.value,
                name: file.name,
                mimeType: file.type,
                sessionID: await claimsDocUploadToken()
            };
            try {
                const data = await ClaimService.uploadDocument(
                    file,
                    documentMetaDataTemplate,
                    authHeader
                );
                const documentsArray = _.get(claimVM, 'documents.value');
                documentsArray.push(data);
                const newClaimVM = _.clone(claimVM);
                _.set(newClaimVM, 'documents.value', documentsArray);
                updateWizardData(newClaimVM);
                setShowLoader(false);
            } catch (error) {
                setShowLoader(false);
                handleError(commonMessages.errorUploadTitle, commonMessages.uploadFailedMessage);
            }
        },
        [claimVM, claimsDocUploadToken, ClaimService, authHeader, updateWizardData, handleError]
    );

    const validateTableForm = useCallback(() => {
        if (
            !claimVM.relatedContacts.aspects.valid
            || !claimVM.relatedContacts.aspects.subtreeValid
        ) {
            return false;
        } else if(!claimVM.officials.aspects.valid
                  || !claimVM.officials.aspects.subtreeValid){
            return false;
        }
        return true;
    }, [claimVM]);

    const overrides = {
        addtionalInformationLoader: {
            loaded: !showLoader
        },
        uploadDocumentGrid: {
            visible: !showLoader
        },
        witnessPartyTable: {
            data: claimVM.relatedContacts.value,
            visible: !_.isEmpty(claimVM.relatedContacts.value)
        },
        officialTable: {
            data: claimVM.officials.value,
            visible: !_.isEmpty(claimVM.officials.value)
        },
        injuredHeader: {
            visible: showInjuredColumn
        },
        ...generateMoreInfoDocumentsOverrides(),
        ...generateMoreInfoAddPersonOverrides()
    };
    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            generateWitnessPartyTableRow: generateNewWitnessRow,
            getFirstNameData: getFirstLastNameData,
            getLastNameData: getFirstLastNameData,
            getInvolvementData: getInvolvementInjuredData,
            getPhoneNumberData: getPhoneNumberData,
            getInjuredData: getInvolvementInjuredData,
            removeContactRow: removeContactRow,
            onUploadDocument: onUploadDocument,
            renderTrigger: generateAddPersonMenuDropDown,
            getWitnessStatementData: getInvolvementInjuredData,
            getWhereWasWitnessData: getInvolvementInjuredData,
            getWitnessPerspectiveData: getFirstLastNameData,
            getOfficialNameData: getOfficialNameData,
            getOfficialReportNumberData: getOfficialNameData,
            getOfficialTypeData: getInvolvementInjuredData,
            generateOfficialTableRow: generateNewOfficialRow,
            removeOfficialRow: removeOfficialRow
        }
    };

    useEffect(() => {
        registerComponentValidation(validateTableForm);
    }, [registerComponentValidation, validateTableForm]);

    return (
        <WizardPage
            cancelLabel={translator(messages.fnolSaveandExit)}
            disableNext={!isComponentValid}
            template={WizardPageTemplate}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={claimVM}
                overrideProps={overrides}
                onModelChange={updateWizardData}
                onValueChange={writeValue}
                onValidationChange={onValidate}
                callbackMap={resolvers.resolveCallbackMap}
                classNameMap={resolvers.resolveClassNameMap}
            />
        </WizardPage>
    );
}
FNOLAdditionalInformationPage.propTypes = wizardProps;
export default withRouter(withAuthenticationContext(FNOLAdditionalInformationPage));
